import { LiveList, LiveMap, LiveObject } from "@liveblocks/client";
import { useList, useMap, useRoom } from "@liveblocks/react";
import { useEffect, useMemo, useState } from "react";

/*
 * A wrapper around useList() that return list data as js array
 * The js array object follows immutable property - the same array
 * would be returned when there are no changed to the LiveList
 */
export function useListWrap<T>(key: string): [T[], LiveList<T> | null] {
  const livelist = useList<T>(key);
  const room = useRoom();
  const [list, setList] = useState(livelist ? livelist.toArray() : null);

  useEffect(() => {
    if (list === null && livelist) {
      setList(livelist.toArray());
    }
  }, [list, livelist]);

  useEffect(() => {
    let unsubscribe: ReturnType<typeof room.subscribe>;

    if (livelist) {
      unsubscribe = room.subscribe(livelist, () => {
        setList(livelist.toArray());
      });
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [livelist, room]);

  const EMPTY = useMemo(() => [], []);

  return [list ?? EMPTY, livelist];
}

export function toObject<V>(
  liveMap: LiveMap<string, LiveObject<V>>
): Record<string, V> {
  const res: Record<string, V> = {};

  for (const [k, v] of liveMap.entries()) {
    res[k] = v.toObject();
  }

  return res;
}

/*
 * A wrapper around useMap() that return a map data in a normal
 * js dictionary object.
 *
 * The object follows immutable property - the same object
 * would be returned when there are no changed to the LiveMap
 */
export function useMapWrap<K extends string, V>(
  key: string,
  opt?: { deep: boolean }
): [Record<K, V>, LiveMap<K, LiveObject<V>> | null] {
  const room = useRoom();
  const mapClient = useMap<K, LiveObject<V>>(key);
  let [map, setMap] = useState<Record<K, V> | null>(null);

  if (map === null && mapClient) {
    map = toObject(mapClient);
    setMap(map);
  }

  useEffect(() => {
    function onMapChange() {
      if (mapClient) {
        const map = toObject(mapClient);
        setMap(map);
      }
    }

    let unsubscribe: ReturnType<typeof room.subscribe>;
    if (mapClient) {
      unsubscribe = opt?.deep
        ? room.subscribe(mapClient, onMapChange, { isDeep: true })
        : room.subscribe(mapClient, onMapChange);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [mapClient]);

  return [map || ({} as Record<K, V>), mapClient];
}
